import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useClient } from '../ClientContext';
import { apiGet, apiPost, apiPostForm, AuthError, GUARDRAILS_URL } from '../api';
import { auth } from '../firebase';
import './reporting/reporting.css';

const money = (pence) => `£${((pence || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const OUTCOME = {
  APPROVED: { cls: 'approved', label: 'Approved' },
  FLAG:     { cls: 'flagged',  label: 'Flagged' },
  BLOCK:    { cls: 'blocked',  label: 'Blocked' },
  BLOCKED:  { cls: 'blocked',  label: 'Blocked' },
};

/** A rule name into something a buyer would say out loud. */
const ruleLabel = (rule) => {
  if (!rule) return 'No rule recorded';
  const known = {
    'audience_rules.min_audience_size': 'Audience below the minimum',
    'audience_rules.min_audience_size_flag': 'Audience below the review threshold',
    'audience_rules.require_exclusion_list': 'No exclusion list',
    'spend_controls.max_daily_budget_pence': 'Daily budget above the cap',
    'spend_controls.review_threshold_pence': 'Daily budget above the review threshold',
    'campaign_structure.naming_convention': 'Campaign name missing the required keyword',
    'campaign_structure.objective_whitelist': 'Objective not permitted',
    'campaign_structure.duplicate_detection': 'Duplicate of a recent campaign',
    'creative_rules.require_approved_creative': 'Creative not approved or not published',
    'creative_rules.creative_must_match_objective': 'Creative built for a different objective',
  };
  return known[rule] || rule;
};


function Issues({ preview }) {
  const { row_errors: rowErrors, conflicts, unknown_creative: unknown } = preview;
  const unknownPages = preview.unknown_pages || [];
  const total = rowErrors.length + conflicts.length + unknown.length + unknownPages.length;
  if (!total) return null;

  return (
    <div className="card bk-issues">
      <div className="card-header">
        <span className="card-title">
          {total} thing{total === 1 ? '' : 's'} to fix before this can be submitted
        </span>
      </div>
      <div className="card-body">
        {rowErrors.length > 0 && (
          <div className="bk-issue-group">
            <div className="bk-issue-k">Rows with problems</div>
            {rowErrors.slice(0, 12).map((r) => (
              <div className="bk-issue" key={r.row_number}>
                <span className="tag-mono">Row {r.row_number}</span>
                <span>{r.errors.join('; ')}</span>
              </div>
            ))}
            {rowErrors.length > 12 && (
              <div className="bk-more">and {rowErrors.length - 12} more rows</div>
            )}
          </div>
        )}

        {conflicts.length > 0 && (
          <div className="bk-issue-group">
            <div className="bk-issue-k">Rows that disagree with each other</div>
            {/* Not resolved silently. Taking the first budget would be a
                decision made on someone's behalf about how much to spend. */}
            {conflicts.slice(0, 12).map((c, i) => (
              <div className="bk-issue" key={i}>
                <span className="tag-mono">Row {c.row_number}</span>
                <span>{c.scope}: {c.message}</span>
              </div>
            ))}
            {conflicts.length > 12 && (
              <div className="bk-more">and {conflicts.length - 12} more</div>
            )}
          </div>
        )}

        {unknown.length > 0 && (
          <div className="bk-issue-group">
            <div className="bk-issue-k">Creative that does not exist or is not published</div>
            <div className="bk-unknown">
              {unknown.map((n) => <span className="tag-mono" key={n}>{n}</span>)}
            </div>
            <div className="bk-more">
              Names must match the Creative Library exactly, and the asset must be approved.
            </div>
          </div>
        )}

        {unknownPages.length > 0 && (
          <div className="bk-issue-group">
            <div className="bk-issue-k">Page ids this ad account cannot use</div>
            <div className="bk-unknown">
              {unknownPages.map((p) => <span className="tag-mono" key={p}>{p}</span>)}
            </div>
            <div className="bk-more">
              Check the id against the Pages in the template. If it ends in 0 and should not,
              the spreadsheet has rounded it — Excel keeps only 15 digits of a number.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function CampaignTree({ campaigns }) {
  const [open, setOpen] = useState({});

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">What this would build</span>
      </div>
      <div className="bk-tree">
        {campaigns.map((c) => (
          <div className="bk-campaign" key={c.campaign_name}>
            <div className="bk-campaign-head">
              <strong>{c.campaign_name}</strong>
              <span className="tag-mono">{c.objective}</span>
              <span className="bk-count">
                {c.adsets.length} ad set{c.adsets.length === 1 ? '' : 's'}
              </span>
            </div>

            {c.adsets.map((a) => {
              const key = `${c.campaign_name}|${a.adset_name}`;
              const expanded = open[key];
              return (
                <div className="bk-adset" key={key}>
                  <button
                    className="bk-adset-head"
                    onClick={() => setOpen({ ...open, [key]: !expanded })}
                  >
                    <span className="bk-chev">{expanded ? '▾' : '▸'}</span>
                    <span className="bk-adset-name">{a.adset_name}</span>
                    <span className="bk-meta">{money(a.daily_budget_pence)}/day</span>
                    {a.audience_size != null && (
                      <span className="bk-meta">{a.audience_size.toLocaleString()} audience</span>
                    )}
                    {a.countries?.length > 0 && (
                      <span className="bk-meta">{a.countries.join(', ')}</span>
                    )}
                    {a.locations?.length === 1 && (
                      <span className="bk-meta">
                        {a.locations[0].radius} {a.locations[0].distance_unit === 'mile' ? 'mi' : 'km'} around {a.locations[0].address_string}
                      </span>
                    )}
                    {a.locations?.length > 1 && (
                      <span className="bk-meta">{a.locations.length} radius locations</span>
                    )}
                    {/* Where and when an ad set runs is exactly what a reviewer
                        needs to see before submitting, not after. */}
                    <span className="bk-meta">
                      {a.start_date || 'On activation'} → {a.end_date || 'Until paused'}
                    </span>
                    <span className="bk-count">
                      {a.ads.length} ad{a.ads.length === 1 ? '' : 's'}
                    </span>
                  </button>

                  {expanded && (
                    <table className="data-table bk-ads">
                      <thead>
                        <tr><th>Row</th><th>Ad</th><th>Creative</th><th>Headline</th></tr>
                      </thead>
                      <tbody>
                        {a.ads.map((ad) => (
                          <tr key={ad.row_number}>
                            <td className="mono">{ad.row_number}</td>
                            <td>{ad.ad_name}</td>
                            <td className="mono">{ad.creative_name}</td>
                            <td style={{ color: 'var(--grey)' }}>{ad.headline || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * Results, grouped by the rule that fired rather than listed one per ad set.
 *
 * A feed of 200 can produce 150 flags, and 150 rows of text is not something
 * anyone reads — they either approve it all without looking or abandon it. But
 * 150 flags are usually three or four distinct problems repeated, and three
 * problems is a review someone will actually do.
 */
function Results({ results, summary }) {
  const [open, setOpen] = useState({});

  const groups = {};
  results.forEach((r) => {
    const key = `${r.outcome}|${r.rule_triggered || ''}`;
    if (!groups[key]) {
      groups[key] = { outcome: r.outcome, rule: r.rule_triggered, reason: r.reason, items: [] };
    }
    groups[key].items.push(r);
  });

  const order = { BLOCK: 0, BLOCKED: 0, FLAG: 1, APPROVED: 2 };
  const sorted = Object.entries(groups).sort(
    (a, b) => (order[a[1].outcome] ?? 3) - (order[b[1].outcome] ?? 3)
  );

  return (
    <>
      <div className="card bk-summary">
        <div className="bk-summary-grid">
          <div><span className="bk-sum-n">{summary.total}</span><span className="bk-sum-k">Ad sets</span></div>
          <div><span className="bk-sum-n bk-green">{summary.created}</span><span className="bk-sum-k">Created</span></div>
          <div><span className="bk-sum-n bk-amber">{summary.flagged}</span><span className="bk-sum-k">Flagged</span></div>
          <div><span className="bk-sum-n bk-red">{summary.blocked}</span><span className="bk-sum-k">Blocked</span></div>
        </div>
        {summary.flagged > 0 && (
          <p className="bk-summary-note">
            Flagged ad sets were not created. They are waiting in the approval queue.
          </p>
        )}
        {summary.meta_errors > 0 && (
          <p className="bk-summary-note bk-warn-text">
            {summary.meta_errors} ad set{summary.meta_errors === 1 ? '' : 's'} passed governance
            but Meta refused to create. Expand below for the reason.
          </p>
        )}
      </div>

      {sorted.map(([key, group]) => {
        const style = OUTCOME[group.outcome] || { cls: '', label: group.outcome };
        const expanded = open[key];
        return (
          <div className="card bk-group" key={key}>
            <button className="bk-group-head" onClick={() => setOpen({ ...open, [key]: !expanded })}>
              <span className="bk-chev">{expanded ? '▾' : '▸'}</span>
              <span className={`pill ${style.cls}`}>{style.label}</span>
              <span className="bk-group-rule">
                {group.outcome === 'APPROVED' ? 'Passed every rule' : ruleLabel(group.rule)}
              </span>
              <span className="bk-count">
                {group.items.length} ad set{group.items.length === 1 ? '' : 's'}
              </span>
            </button>

            {expanded && (
              <>
                {group.reason && group.outcome !== 'APPROVED' && (
                  <div className="bk-group-reason">{group.reason}</div>
                )}
                <table className="data-table">
                  <thead>
                    <tr><th>Campaign</th><th>Ad set</th><th>Ads</th><th>Rows</th><th>Result</th></tr>
                  </thead>
                  <tbody>
                    {group.items.map((r, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 500 }}>{r.campaign_name}</td>
                        <td>{r.adset_name}</td>
                        <td>{r.ad_count}</td>
                        <td className="mono" style={{ fontSize: 12, color: 'var(--grey)' }}>
                          {r.source_rows[0]}–{r.source_rows[r.source_rows.length - 1]}
                        </td>
                        <td>
                          {r.created ? (
                            <span className="mono bk-created">Campaign {r.campaign_id}</span>
                          ) : r.meta_error ? (
                            <span className="bk-warn-text">{r.meta_error}</span>
                          ) : (
                            <span style={{ color: 'var(--grey)' }}>Not created</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        );
      })}
    </>
  );
}



const STATE_LABEL = {
  created:         { cls: 'approved', label: 'Created' },
  approved:        { cls: 'approved', label: 'Approved' },
  awaiting_review: { cls: 'flagged',  label: 'Awaiting review' },
  rejected:        { cls: 'blocked',  label: 'Rejected' },
  blocked:         { cls: 'blocked',  label: 'Blocked' },
};

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Past uploads.
 *
 * An ad set that was flagged and has since been decided reads as the decision,
 * not as "flagged" — status comes from the server on every load rather than
 * from what was true at upload time.
 */
function History({ batches, loading }) {
  const [open, setOpen] = useState({});

  if (loading) {
    return (
      <div className="card">
        <div className="card-header"><span className="card-title">Previous uploads</span></div>
        <div className="card-body">
          <span style={{ fontSize: 13, color: 'var(--grey)' }}>Loading…</span>
        </div>
      </div>
    );
  }

  if (!batches.length) {
    return (
      <div className="card">
        <div className="card-header"><span className="card-title">Previous uploads</span></div>
        <div className="empty-state" style={{ padding: 'var(--space-8) var(--space-6)' }}>
          <h3>No uploads yet</h3>
          <p>Feeds you submit will be listed here with what happened to each ad set.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bk-history-head">Previous uploads</div>

      {batches.map((b) => {
        const s = b.summary;
        const expanded = open[b.batch_id];

        // What still needs a person, said first — it is the only part of a
        // past upload anyone has to act on.
        const outstanding = s.awaiting_review;

        return (
          <div className="card bk-group" key={b.batch_id}>
            <button
              className="bk-group-head"
              onClick={() => setOpen({ ...open, [b.batch_id]: !expanded })}
            >
              <span className="bk-chev">{expanded ? '▾' : '▸'}</span>
              <span className="bk-hist-when">{when(b.uploaded_at)}</span>
              <span className="bk-hist-counts">
                {s.total} ad set{s.total === 1 ? '' : 's'} · {s.ads} ad{s.ads === 1 ? '' : 's'}
              </span>
              <span className="bk-hist-pills">
                {s.created > 0 && <span className="pill approved">{s.created} created</span>}
                {s.approved > 0 && <span className="pill approved">{s.approved} approved</span>}
                {outstanding > 0 && (
                  <span className="pill flagged">{outstanding} awaiting review</span>
                )}
                {s.rejected > 0 && <span className="pill blocked">{s.rejected} rejected</span>}
                {s.blocked > 0 && <span className="pill blocked">{s.blocked} blocked</span>}
              </span>
            </button>

            {expanded && (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Campaign</th><th>Ad set</th><th>Ads</th>
                    <th>Status</th><th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {b.adsets.map((a) => {
                    const st = STATE_LABEL[a.state] || { cls: '', label: a.state };
                    return (
                      <tr key={a.action_id}>
                        <td style={{ fontWeight: 500 }}>{a.campaign_name}</td>
                        <td>{a.adset_name}</td>
                        <td>{a.ad_count}</td>
                        <td><span className={`pill ${st.cls}`}>{st.label}</span></td>
                        <td style={{ fontSize: 12.5, color: 'var(--grey)' }}>
                          {a.decided_by
                            ? `${a.state === 'rejected' ? 'Rejected' : 'Approved'} by ${a.decided_by}`
                            : (a.reason || '—')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </>
  );
}


export default function BulkCampaigns() {
  const { selectedId, selected, isAll } = useClient();

  const [feedText, setFeedText] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const fileRef = useRef(null);

  const loadHistory = useCallback(() => {
    if (!selectedId || isAll) { setHistoryLoading(false); return; }
    setHistoryLoading(true);
    apiGet(`/bulk/history?client_id=${selectedId}`)
      .then((d) => { setHistory(d.batches || []); setHistoryLoading(false); })
      .catch(() => { setHistory([]); setHistoryLoading(false); });
  }, [selectedId, isAll]);

  // Reloaded whenever the client changes, and again after a submit, so a fresh
  // upload appears without a manual refresh.
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const downloadTemplate = async () => {
    setError(null);
    try {
      // Fetched rather than linked, because the endpoint needs an auth header
      // and an anchor tag cannot carry one.
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        `${GUARDRAILS_URL}/bulk/template?client_id=${selectedId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Could not generate a template.');
      const blob = await response.blob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bidsense-bulk-${(selected?.name || 'client').replace(/\s+/g, '-').toLowerCase()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Could not generate a template.');
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setResults(null);
    setBusy(true);
    setFileName(file.name);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('client_id', selectedId);

      const data = await apiPostForm('/bulk/preview', formData);
      // Submit sends back the server's reading of the file, not the file. An
      // Excel upload is binary, and sending the rows the preview showed means
      // submit evaluates exactly what was reviewed.
      setFeedText(data.normalized_feed);
      setPreview(data);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : (err.message || 'Could not read that file.'));
      setPreview(null);
    }
    setBusy(false);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const data = await apiPost('/bulk/submit', {
        client_id: selectedId,
        feed: feedText,
      });
      setResults(data);
      setPreview(null);
      loadHistory();
    } catch (err) {
      setError(err.message || 'The upload could not be submitted.');
    }
    setSubmitting(false);
  };

  const reset = () => {
    setFeedText(null); setFileName(null); setPreview(null);
    setResults(null); setError(null);
  };

  if (isAll) return (
    <>
      <div className="page-header">
        <h1 className="page-title">Bulk Campaigns</h1>
        <p className="page-subtitle">Campaigns are built for one client at a time</p>
      </div>
      <div className="card">
        <div className="empty-state">
          <h3>Select a client</h3>
          <p>Choose a client from the switcher above to upload a campaign feed.</p>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Bulk Campaigns</h1>
          <p className="page-subtitle">
            Upload a feed to build many campaigns for {selected?.name || 'this client'}. Every ad
            set is checked against their governance rules, and nothing is created until you submit.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flex: 'none' }}>
          <button className="btn btn-secondary" onClick={downloadTemplate}>
            Download template
          </button>
          {(preview || results) && (
            <button className="btn btn-secondary" onClick={reset}>Start again</button>
          )}
        </div>
      </div>

      {error && (
        <div className="card" style={{ marginBottom: 'var(--space-5)', padding: '12px 20px', borderLeft: '3px solid var(--red)' }}>
          <div style={{ fontSize: 13, color: 'var(--red)', fontWeight: 600 }}>{error}</div>
        </div>
      )}

      {!preview && !results && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current?.click()}
          className="bk-drop"
          style={{
            borderColor: dragOver ? 'var(--brass)' : 'var(--grey-light)',
            background: dragOver ? 'var(--amber-bg)' : 'var(--white)',
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.csv"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
          <div style={{ fontSize: 30, marginBottom: 10 }}>⊞</div>
          <div className="bk-drop-title">
            {busy ? 'Reading your feed…' : 'Drop your feed here, Excel or CSV, or click to browse'}
          </div>
          <div className="bk-drop-sub">
            One row per ad. Repeat campaign and ad set values on every row.
            Download the template if you need a starting point.
          </div>
        </div>
      )}

      {preview && (
        <>
          <div className="card bk-summary">
            <div className="bk-summary-grid">
              <div><span className="bk-sum-n">{preview.row_count}</span><span className="bk-sum-k">Rows</span></div>
              <div><span className="bk-sum-n">{preview.campaign_count}</span><span className="bk-sum-k">Campaigns</span></div>
              <div><span className="bk-sum-n">{preview.adset_count}</span><span className="bk-sum-k">Ad sets</span></div>
              <div><span className="bk-sum-n">{preview.ad_count}</span><span className="bk-sum-k">Ads</span></div>
            </div>
            <p className="bk-summary-note">
              From {fileName}. Nothing has been created or evaluated yet.
            </p>
          </div>

          <Issues preview={preview} />
          <CampaignTree campaigns={preview.campaigns} />

          <div className="bk-submit">
            <button
              className="btn btn-brass"
              onClick={submit}
              disabled={!preview.ready || submitting}
            >
              {submitting
                ? `Evaluating ${preview.adset_count} ad sets…`
                : `Check and build ${preview.adset_count} ad set${preview.adset_count === 1 ? '' : 's'}`}
            </button>
            {!preview.ready && (
              <span className="bk-submit-note">
                Fix the issues above, then upload the corrected file.
              </span>
            )}
            {preview.ready && (
              <span className="bk-submit-note">
                Each ad set is checked against the governance rules. Approved ones are created
                paused; flagged ones go to the approval queue.
              </span>
            )}
          </div>
        </>
      )}

      {results && <Results results={results.results} summary={results.summary} />}

      <History batches={history} loading={historyLoading} />

      <p className="rp-disclaimer">
        Campaigns are created paused and must be activated in Ads Manager. Ad sets a rule flags
        are not created — they are held in the approval queue.
      </p>
    </>
  );
}